import {
    CabinetMesh,
    RoomPlannerProduct,
    UndoActionContext,
} from 'components/customer/RoomPlanner/types';
import cameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import {store} from 'store/customer';
import {
    addToHistoryStack,
    duplicateIdsSet,
    setBackupProductValues,
    setProductValuesForUpdate,
    getProducts,
    setIsBusy,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {dispatchCabinetAddToHistory} from 'components/customer/RoomPlanner/helpers/history/cabinetAddAction';
import {formatHistoryTitlePrefix} from 'components/customer/RoomPlanner/helpers/history/formatHistoryTitle';
import {cloneDeep} from 'lodash';
import {mapProductData} from 'components/customer/Product/helpers/mapProductData';
import {ProductDataStore} from 'components/customer/Preview3D/usePreview3DData';
import {
    PreviewFormValues,
    ShelfType,
} from 'components/customer/Preview3D/types';
import {AppDispatch} from 'store/customer/storeSetup';
import {mapMaterial} from 'components/customer/Materials/helper/materialHelper';
import {mapDoor} from 'components/customer/Materials/helper/doorHelper';
import {detectShakerDoor} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {MaterialType, Material} from 'components/customer/Materials/entity';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {normalizeRawProductValues} from 'components/customer/RoomPlanner/helpers/normalizeRawProductValues';

interface ProductChangeItem {
    key: string;
    name: string;
    room_data: string | number | null;
    product_data: string | number | null;
    value: unknown;
    is_saved?: boolean;
    set_key?: string;
    image?: string;
    apply_to_room?: boolean;
}

interface ProductChangeCategory {
    category: string;
    items: ProductChangeItem[];
}

const getProductToProductChangedData = (
    previous: RoomPlannerProduct,
    current: RoomPlannerProduct
): {categories: ProductChangeCategory[]; flatList: ProductChangeItem[]} => {
    const categories: Record<string, ProductChangeCategory> = {};
    const flatList: ProductChangeItem[] = [];

    const addItem = (categoryName: string, item: ProductChangeItem) => {
        flatList.push(item);

        if (!categories[String(categoryName)]) {
            categories[String(categoryName)] = {
                category: categoryName,
                items: [],
            };
        }
        categories[String(categoryName)].items.push(item);
    };

    const boolLabel = (
        value: unknown,
        trueLabel: string,
        falseLabel: string
    ) => (value ? trueLabel : falseLabel);

    // Supply Default Selection
    if (
        previous.cabinet_include_hardware !== current.cabinet_include_hardware
    ) {
        addItem('Supply Default Selection', {
            key: 'exclude_hardware',
            name: 'Include Hardware',
            is_saved: true,
            set_key: 'isExcludeHardwareSet',
            room_data: boolLabel(
                previous.cabinet_include_hardware,
                'Supply Hardware',
                'No Hardware (Drill Only)'
            ),
            product_data: boolLabel(
                current.cabinet_include_hardware,
                'Supply Hardware',
                'No Hardware (Drill Only)'
            ),
            // For historical consistency, value is the inverse (exclude flag)
            value: !current.cabinet_include_hardware,
            apply_to_room: true,
        });
    }

    if (
        previous.cabinet_include_assembly !== current.cabinet_include_assembly
    ) {
        addItem('Supply Default Selection', {
            key: 'include_assembly',
            name: 'Include Assembly',
            is_saved: true,
            set_key: 'isIncludeAssemblySet',
            room_data: boolLabel(
                previous.cabinet_include_assembly,
                'Include assembly',
                'No assembly'
            ),
            product_data: boolLabel(
                current.cabinet_include_assembly,
                'Include assembly',
                'No assembly'
            ),
            value: current.cabinet_include_assembly,
            apply_to_room: true,
        });
    }

    if (previous.cabinet_adjustable_legs !== current.cabinet_adjustable_legs) {
        addItem('Supply Default Selection', {
            key: 'adjustable_legs',
            name: 'Adjustable Legs',
            is_saved: true,
            set_key: 'isAdjustableLegsSet',
            room_data: boolLabel(
                previous.cabinet_adjustable_legs,
                'With adjustable legs',
                'No adjustable legs'
            ),
            product_data: boolLabel(
                current.cabinet_adjustable_legs,
                'With adjustable legs',
                'No adjustable legs'
            ),
            value: current.cabinet_adjustable_legs,
            apply_to_room: true,
        });
    }

    const measurement = 'mm';

    // Exterior Material Selection
    if (previous.cabinet_ext_colour !== current.cabinet_ext_colour) {
        addItem('Exterior Material Selection', {
            key: 'ext_colour',
            name: 'Exterior Material Colour',
            is_saved: true,
            set_key: 'isExteriorColourSet',
            room_data:
                (previous as {exterior_colour_name?: string})
                    .exterior_colour_name ??
                previous.cabinet_ext_colour ??
                null,
            product_data:
                (current as {exterior_colour_name?: string})
                    .exterior_colour_name ??
                current.cabinet_ext_colour ??
                null,
            value: current.cabinet_ext_colour,
            apply_to_room: true,
        });
    }

    if (previous.cabinet_ext_edge_colour !== current.cabinet_ext_edge_colour) {
        addItem('Exterior Material Selection', {
            key: 'ext_edge_colour',
            name: 'Exterior Edge Colour',
            is_saved: true,
            set_key: 'isExteriorEdgeColourSet',
            room_data: previous.cabinet_ext_edge_colour ?? null,
            product_data: current.cabinet_ext_edge_colour ?? null,
            value: current.cabinet_ext_edge_colour,
            apply_to_room: true,
        });
    }

    // Base Product Selection
    if (previous.cabinet_height !== current.cabinet_height) {
        addItem('Base Product Selection', {
            key: 'base_height',
            name: 'Base Height',
            is_saved: true,
            set_key: 'isBaseHeightSet',
            room_data:
                typeof previous.cabinet_height === 'number'
                    ? `${previous.cabinet_height} ${measurement}`
                    : (previous.cabinet_height ?? null),
            product_data:
                typeof current.cabinet_height === 'number'
                    ? `${current.cabinet_height} ${measurement}`
                    : (current.cabinet_height ?? null),
            value: current.cabinet_height,
            apply_to_room: true,
        });
    }

    if (previous.cabinet_depth !== current.cabinet_depth) {
        addItem('Base Product Selection', {
            key: 'base_depth',
            name: 'Base Depth',
            is_saved: true,
            set_key: 'isBaseDepthSet',
            room_data:
                typeof previous.cabinet_depth === 'number'
                    ? `${previous.cabinet_depth} ${measurement}`
                    : (previous.cabinet_depth ?? null),
            product_data:
                typeof current.cabinet_depth === 'number'
                    ? `${current.cabinet_depth} ${measurement}`
                    : (current.cabinet_depth ?? null),
            value: current.cabinet_depth,
            apply_to_room: true,
        });
    }

    return {
        categories: Object.values(categories),
        flatList,
    };
};

export const updateUserData = (
    cabinetGroups: CabinetMesh[],
    jobCabinetId: number
) => {
    // attach correct job id to cabinet groups
    if (cabinetGroups && Array.isArray(cabinetGroups)) {
        const indexes: number[] = [];
        cabinetGroups.forEach((cabinetGroup, index) => {
            indexes.push(index);
            cabinetGroup.userData.id = `${jobCabinetId}-${index}`;
            cabinetGroup.userData.values = {
                ...cabinetGroup.userData.values,
                duplicate_id: `${jobCabinetId}-${index}`,
                job_cabinet_id: jobCabinetId,
                id: jobCabinetId,
            };
        });

        store.dispatch(duplicateIdsSet(jobCabinetId, indexes));
    }
};

export const removeMeshFromScene = (
    cabinetGroups: CabinetMesh[],
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>
) => {
    if (cabinetGroups && cameraSetup.scene) {
        if (Array.isArray(cabinetGroups)) {
            cabinetGroups.forEach((cabinetGroup) => {
                deleteCabinetMesh(cabinetGroup, false, cabinetGroupListRef);
            });
        }
    }
};

interface AddToHistoryStackParams {
    isUpdate: boolean;
    cabinetId: number;
    mappedValues: RoomPlannerProduct & {cabinet_name?: string};
    backupProduct: PreviewFormValues;
    cabinet: CabinetMesh | null;
    productFormSubmitHandler: (
        values: PreviewFormValues,
        ignoreHistoryDispatch?: boolean
    ) => Promise<void>;
    dispatch: AppDispatch;
    roomId: number;
    productDataStore: React.MutableRefObject<ProductDataStore>;
    shelvesStructureStore: React.MutableRefObject<Record<string, unknown>>;
    shelfTypes: React.MutableRefObject<ShelfType[]>;
    shelfStyles: React.MutableRefObject<Record<string, unknown>>;
    shakerDoorInfo?: {isShakerDoor: boolean};
    material?: unknown;
    door?: unknown;
}

interface CabinetUpdateUndoParams {
    previousValues: RoomPlannerProduct & PreviewFormValues;
    backupProduct: PreviewFormValues;
    additionalPayload: Record<string, unknown>;
    dispatch: AppDispatch;
    productFormSubmitHandler: (
        values: PreviewFormValues,
        ignoreHistoryDispatch?: boolean
    ) => Promise<void>;
}

const createCabinetUpdateUndoAction = ({
    previousValues,
    backupProduct,
    additionalPayload,
    dispatch,
    productFormSubmitHandler,
}: CabinetUpdateUndoParams): ((ctx?: UndoActionContext) => Promise<void>) => {
    return async (ctx) => {
        if (!ctx) return;
        previousValues.job_cabinet_id = Number(
            ctx.historyItem?.data?.id ?? previousValues.job_cabinet_id
        );
        const disp = ctx.dispatch ?? dispatch;

        const existingCabinet = ctx.cabinetGroupListRef?.current?.find(
            (cabinet) =>
                Number(cabinet.userData?.values?.job_cabinet_id) ==
                Number(previousValues.job_cabinet_id)
        );

        if (!existingCabinet) return;

        disp(setIsBusy(true));
        try {
            await productFormSubmitHandler(previousValues, true);
        } finally {
            setTimeout(() => {
                disp(setIsBusy(false));
            }, 100);
        }

        const materialFromValues =
            previousValues.ext_material != null
                ? mapMaterial(previousValues.ext_material)
                : null;
        const doorFromValues =
            previousValues.door != null ? mapDoor(previousValues.door) : null;

        if (backupProduct) {
            const historyData = ctx.historyItem?.data as
                | {
                      material?: Material | null;
                      door?: ReturnType<typeof getDoor> | null;
                  }
                | undefined;

            // Handle bug with shelves count and simple shelves mismatch
            const computedShelvesCount =
                (previousValues.cabinet_simple_shelves ||
                    typeof previousValues.cabinet_simple_shelves ===
                        'undefined') &&
                previousValues.shelves &&
                previousValues.shelves.length !==
                    previousValues.simple_shelves_amount
                    ? previousValues.shelves.length
                    : previousValues.simple_shelves_amount;

            const backup = {
                ...backupProduct,
                fromBackup: true,
                ...additionalPayload,
                job_cabinet_id: Number(previousValues.job_cabinet_id),
                id: Number(previousValues.job_cabinet_id),
                shakerDoorInfo:
                    (
                        backupProduct as unknown as {
                            shakerDoorInfo?: {isShakerDoor: boolean};
                        }
                    ).shakerDoorInfo ??
                    detectShakerDoor(
                        materialFromValues ?? historyData?.material,
                        doorFromValues ?? historyData?.door
                    ),
                material:
                    materialFromValues ??
                    (ctx.historyItem?.data as {material?: unknown})?.material,
                door:
                    doorFromValues ??
                    (ctx.historyItem?.data as {door?: unknown})?.door,
                fromUndoOrRedo: true,
                simple_shelves_amount: computedShelvesCount,
            } as unknown as PreviewFormValues;
            dispatch(setProductValuesForUpdate(backup));
            dispatch(setBackupProductValues(null));
        }
    };
};

interface CabinetUpdateRedoParams {
    mappedValues: RoomPlannerProduct & {cabinet_name?: string};
    cabinet: CabinetMesh;
    additionalPayload: Record<string, unknown>;
    dispatch: AppDispatch;
    productFormSubmitHandler: (
        values: PreviewFormValues,
        ignoreHistoryDispatch?: boolean
    ) => Promise<void>;
    shakerDoorInfo?: {isShakerDoor: boolean};
    materialForShaker?: unknown;
    doorForShaker?: unknown;
}

const createCabinetUpdateRedoAction = ({
    mappedValues,
    cabinet,
    additionalPayload,
    dispatch,
    productFormSubmitHandler,
    shakerDoorInfo,
}: CabinetUpdateRedoParams): ((ctx?: UndoActionContext) => Promise<void>) => {
    return async (ctx) => {
        if (!ctx) return;
        mappedValues.job_cabinet_id = Number(
            ctx.historyItem?.data?.id ?? mappedValues.job_cabinet_id
        );
        const disp = ctx.dispatch ?? dispatch;

        disp(setIsBusy(true));
        try {
            await productFormSubmitHandler(
                mappedValues as unknown as PreviewFormValues,
                true
            );
        } finally {
            setTimeout(() => {
                disp(setIsBusy(false));
            }, 100);
        }

        const cabinetValues = cabinet.userData.values as unknown as {
            shakerDoorInfo?: {isShakerDoor: boolean};
            material?: unknown;
            door?: unknown;
        };
        const historyData = ctx.historyItem?.data as
            | {
                  material?: unknown;
                  door?: unknown;
              }
            | undefined;
        const backup = {
            ...cabinet.userData.values,
            fromBackup: true,
            ...additionalPayload,
            job_cabinet_id: Number(mappedValues.job_cabinet_id),
            id: Number(mappedValues.job_cabinet_id),
            shakerDoorInfo: cabinetValues.shakerDoorInfo ?? shakerDoorInfo,
            material: cabinetValues.material ?? historyData?.material,
            door: cabinetValues.door ?? historyData?.door,
            fromUndoOrRedo: true,
        } as unknown as PreviewFormValues;
        dispatch(setProductValuesForUpdate(backup));
        dispatch(setBackupProductValues(null));
    };
};

export const applyDoorDefaultsToProduct = (
    product: Record<string, unknown> & {
        doors?: unknown[];
        glassSubPanels?: boolean[][];
        hori_mid_rail_positions?: unknown;
        vert_mid_rail_positions?: unknown;
    }
): void => {
    if (!product.doors || !Array.isArray(product.doors) || !product.doors[0]) {
        return;
    }
    const door = product.doors[0] as Record<string, unknown>;
    product.door_hang_type = door.door_hang_type == 0 ? 1 : door.door_hang_type;
    product.door_hang = door.door_hang;
    product.border_width_top = door.border_width_top;
    product.border_width_bottom = door.border_width_bottom;
    product.border_width_left = door.border_width_left;
    product.border_width_right = door.border_width_right;
    product.hori_amount = door.hori_amount ?? 0;
    product.hori_height = door.hori_height;
    product.vert_amount = door.vert_amount ?? 0;
    product.vert_width = door.vert_width;
    product.no_vert_bars = door.no_vert_bars;
    product.no_hori_bars = door.no_hori_bars;
    product.bar_width = door.bar_width;
    product.panel_edge_top = door.panel_edge_top;
    product.panel_edge_bottom = door.panel_edge_bottom;
    product.panel_edge_left = door.panel_edge_left;
    product.panel_edge_right = door.panel_edge_right;
    product.cabinet_edge_l1 = door.panel_edge_top;
    product.cabinet_edge_l2 = door.panel_edge_bottom;
    product.cabinet_edge_w1 = door.panel_edge_left;
    product.cabinet_edge_w2 = door.panel_edge_right;
    const panelWidth =
        product.cabinet_panel_width ?? product.width ?? product.cabinet_width;
    product.cabinet_panel_width = panelWidth;
    product.cabinet_width_door_1 = panelWidth;
    product.cabinet_width_door_2 = 0;
    if (product.hasOwnProperty('drill_only_hinge_details')) {
        const details = product.drill_only_hinge_details as
            | {drill_only_hinge?: unknown; name?: unknown}
            | undefined;
        if (details) {
            product.drill_only_hinge = details.drill_only_hinge;
            product.drill_only_hinge_name = details.name;
        }
    }
    product.glassSubPanelEnabledPositions = {
        sub_panel_1: false,
        sub_panel_enabled_1: false,
        sub_panel_2: false,
        sub_panel_enabled_2: false,
        sub_panel_3: false,
        sub_panel_enabled_3: false,
        sub_panel_4: false,
        sub_panel_enabled_4: false,
        sub_panel_5: false,
        sub_panel_enabled_5: true,
        sub_panel_6: false,
        sub_panel_enabled_6: false,
        sub_panel_7: false,
        sub_panel_enabled_7: false,
        sub_panel_8: false,
        sub_panel_enabled_8: false,
        sub_panel_9: false,
        sub_panel_enabled_9: false,
    };
    if (!product.glassSubPanels) {
        product.glassSubPanels = [
            [false, false, false],
            [false, false, false],
            [false, false, false],
        ];
    }
    if (door.sub_panel1 == 1) product.glassSubPanels[0][0] = true;
    if (door.sub_panel2 == 1) product.glassSubPanels[0][1] = true;
    if (door.sub_panel3 == 1) product.glassSubPanels[0][2] = true;
    if (door.sub_panel4 == 1) product.glassSubPanels[1][0] = true;
    if (door.sub_panel5 == 1) product.glassSubPanels[1][1] = true;
    if (door.sub_panel6 == 1) product.glassSubPanels[1][2] = true;
    if (door.sub_panel7 == 1) product.glassSubPanels[2][0] = true;
    if (door.sub_panel8 == 1) product.glassSubPanels[2][1] = true;
    if (door.sub_panel9 == 1) product.glassSubPanels[2][2] = true;
    const horiPositions = door.horizontal_positions as number[] | undefined;
    if (
        door.hasOwnProperty('horizontal_positions') &&
        Array.isArray(horiPositions) &&
        horiPositions.length > 0
    ) {
        horiPositions.forEach((position: number, index: number) => {
            product[`rail_hori_${index + 1}`] = position;
        });
    }
    product.hori_mid_rail_positions = horiPositions;
    const vertPositions = door.vertical_positions as number[] | undefined;
    if (
        door.hasOwnProperty('vertical_positions') &&
        Array.isArray(vertPositions) &&
        vertPositions.length > 0
    ) {
        vertPositions.forEach((position: number, index: number) => {
            product[`rail_vert_${index + 1}`] = position;
        });
    }
    product.vert_mid_rail_positions = vertPositions;
};

interface HandlePostSaveRoomPlannerActionsParams {
    isRoomPlannerPage: boolean;
    ignoreHistoryDispatch: boolean;
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>;
    responseId: number;
    mappedValues: RoomPlannerProduct & {cabinet_name?: string};
    isUpdate: boolean;
    backupProduct: PreviewFormValues;
    productFormSubmitHandler: (
        values: PreviewFormValues,
        ignoreHistoryDispatch?: boolean
    ) => Promise<void>;
    dispatch: AppDispatch;
    roomId: number;
    productDataStore: React.MutableRefObject<ProductDataStore>;
    shelvesStructureStore: React.MutableRefObject<Record<string, unknown>>;
    shelfTypes: React.MutableRefObject<ShelfType[]>;
    shelfStyles: React.MutableRefObject<Record<string, unknown>>;
}

export const handlePostSaveRoomPlannerActions = ({
    isRoomPlannerPage,
    ignoreHistoryDispatch,
    cabinetGroupListRef,
    responseId,
    mappedValues,
    isUpdate,
    backupProduct,
    productFormSubmitHandler,
    dispatch,
    roomId,
    productDataStore,
    shelvesStructureStore,
    shelfTypes,
    shelfStyles,
}: HandlePostSaveRoomPlannerActionsParams): void => {
    if (!isRoomPlannerPage) return;

    const cabinet = cabinetGroupListRef?.current?.find(
        (group) => group.userData.values.id == responseId
    );

    if (!ignoreHistoryDispatch) {
        const state = store.getState();
        const materialForDetection = getMaterial(
            state,
            MaterialType.EXTERIOR,
            0
        );
        const doorForDetection = getDoor(state, MaterialType.EXTERIOR, 0);
        const shakerDoorInfo = detectShakerDoor(
            materialForDetection,
            doorForDetection
        );

        addToHistoryStackForProduct({
            isUpdate,
            cabinetId: responseId,
            mappedValues,
            backupProduct,
            cabinet: cabinet || null,
            productFormSubmitHandler,
            dispatch,
            roomId,
            productDataStore,
            shelvesStructureStore,
            shelfTypes,
            shelfStyles,
            shakerDoorInfo,
            material: materialForDetection,
            door: doorForDetection,
        });
    }

    const cabinetValues = cabinet?.userData.values ?? mappedValues;
    dispatch(
        setProductValuesForUpdate({
            ...cabinetValues,
            sideEffectFromUpdate: true,
        } as unknown as PreviewFormValues)
    );
};

export const addToHistoryStackForProduct = ({
    isUpdate,
    cabinetId,
    mappedValues,
    backupProduct,
    cabinet,
    productFormSubmitHandler,
    dispatch,
    roomId,
    productDataStore,
    shelfTypes,
    shelfStyles,
    shelvesStructureStore,
    shakerDoorInfo,
    material: materialForShaker,
    door: doorForShaker,
}: AddToHistoryStackParams) => {
    if (isUpdate) {
        // Handle CABINET_UPDATE
        if (cabinet) {
            const roomIndex = store.getState().roomPlanner.roomIndex;
            const roomCabNumber = cabinet.userData.values.room_cab_number;
            const previousValues = mapProductData({
                values: cloneDeep(backupProduct),
                productDataStore,
                shelvesStructureStore,
                shelfTypes,
                shelfStyles,
            }) as RoomPlannerProduct & PreviewFormValues;
            previousValues.room_id = roomId;
            delete previousValues.template_3d;

            applyDoorDefaultsToProduct(
                previousValues as unknown as Parameters<
                    typeof applyDoorDefaultsToProduct
                >[0]
            );

            const {flatList: productChangeFlatList} =
                getProductToProductChangedData(
                    previousValues as RoomPlannerProduct,
                    mappedValues as RoomPlannerProduct
                );

            let changeSuffix = '';
            if (productChangeFlatList.length === 1) {
                changeSuffix = ` *(changed ${productChangeFlatList[0].name})*`;
            } else if (productChangeFlatList.length === 2) {
                changeSuffix = ` *(changed ${productChangeFlatList[0].name} & ${productChangeFlatList[1].name})*`;
            } else if (productChangeFlatList.length > 2) {
                changeSuffix = ` *(changed ${productChangeFlatList.length} details)*`;
            }

            const baseTitle =
                `**Update** ${formatHistoryTitlePrefix(roomIndex, roomCabNumber)} ${mappedValues.cabinet_name ?? ''}`.trimEnd();
            const title = `${baseTitle}${changeSuffix}`;

            normalizeRawProductValues(
                previousValues as unknown as Record<string, unknown>
            );

            const cabinetPositionY = cabinet.position.y;
            const newHeight = Number(mappedValues.cabinet_height);
            const additionalPayload = cabinet.userData.isUpperProduct
                ? {
                      _restorePositionY: cabinetPositionY,
                      _restoreHeight: newHeight,
                  }
                : {};

            dispatch(
                addToHistoryStack({
                    action: 'CABINET_UPDATE',
                    isActive: true,
                    timestamp: Date.now(),
                    title,
                    data: {
                        id: cabinetId,
                        shakerDoorInfo,
                        material: materialForShaker,
                        door: doorForShaker,
                        undoAction: createCabinetUpdateUndoAction({
                            previousValues,
                            backupProduct,
                            additionalPayload,
                            dispatch,
                            productFormSubmitHandler,
                        }),
                        redoAction: createCabinetUpdateRedoAction({
                            mappedValues,
                            cabinet,
                            additionalPayload,
                            dispatch,
                            productFormSubmitHandler,
                            shakerDoorInfo,
                            materialForShaker,
                            doorForShaker,
                        }),
                    },
                })
            );
        }
    } else {
        // Handle CABINET_ADD
        const state = store.getState();
        const roomIndex = state.roomPlanner.roomIndex;
        const roomPlannerProducts = getProducts(state) ?? [];
        const roomCabNumber = roomPlannerProducts.length + 1;

        dispatchCabinetAddToHistory(dispatch, {
            title: `**Add** ${formatHistoryTitlePrefix(roomIndex, roomCabNumber)} ${mappedValues.cabinet_name ?? ''} From Product Editor`.trimEnd(),
            data: {
                id: cabinetId,
                shakerDoorInfo,
                material: materialForShaker,
                door: doorForShaker,
                redoAction: async () => {
                    dispatch(setIsBusy(true));

                    try {
                        await productFormSubmitHandler(
                            mappedValues as unknown as PreviewFormValues,
                            true
                        );
                    } finally {
                        setTimeout(() => {
                            dispatch(setIsBusy(false));
                        }, 100);
                    }
                },
            },
        });
    }
};
